// src/components/SpendingsAndIncomeTracker.jsx
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from "chart.js";
import { FinanceContext } from "../contexts/FinanceContext";
import { format } from "date-fns";
import { useCurrency } from "../utils/useCurrency";
import { useTranslation } from "react-i18next";

// React Icons
import {
  FaUtensils,
  FaCoffee,
  FaHome,
  FaCar,
  FaTshirt,
  FaLaptop,
  FaBook,
  FaGift,
  FaPlane,
  FaHeartbeat,
  FaGem,
} from "react-icons/fa";
import { IoGameController } from "react-icons/io5";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip
);

// ---------- MANUAL CATEGORY ICONS ----------
const categoryIconMap = {
  "Food / Drinks": FaUtensils,
  "Home / Rent": FaHome,
  "Car & Fuel": FaCar,
  Clothes: FaTshirt,
  Entertainment: IoGameController,
  Education: FaBook,
  Gifts: FaGift,
  Travel: FaPlane,
  Health: FaHeartbeat,
  Beauty: FaGem,
};

// *** NEW STYLED COMPONENT FOR BOTTOM-RIGHT LINK ***
const DetailsLink = styled.a`
  position: absolute;
  bottom: 25px;
  right: 25px;
  font-size: 0.85rem;
  color: var(--fontSecondaryColor);
  text-decoration: underline;
  cursor: pointer;
  z-index: 10;
  transition: color 0.2s;

  &:hover {
    color: #38bdf8;
  }
`;

export default function SpendingsAndIncomeTracker({ theme }) {
  const {
    history,
    categories: userCategories,
    loading,
    addIncome,
    addSpending,
    addCategory,
    deleteCategory,
  } = useContext(FinanceContext);

  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();

  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showSpendingModal, setShowSpendingModal] = useState(false);

  // derived totals
  const totals = useMemo(() => {
    let totalIncome = 0;
    let totalSpending = 0;

    history.forEach((entry) => {
      const amt = Math.abs(Number(entry.amount || 0));
      if (entry.type === "income") totalIncome += amt;
      else if (entry.type === "spending") totalSpending += amt;
    });

    const currentTotal = totalIncome - totalSpending;
    return { currentTotal, totalIncome, totalSpending };
  }, [history]);

  const lineColor = "#38bdf8";
  const gridColor = theme === "dark" ? "#363d52" : "#ddd";

  // prepare chart data
  const chartData = useMemo(() => {
    const labels = history.map((h) => format(new Date(h.date), "MMM dd"));
    const data = history.map((h) =>
      h.type === "income"
        ? Math.abs(Number(h.amount || 0))
        : -Math.abs(Number(h.amount || 0))
    );

    let balance = 0;
    const running = data.map((v) => (balance += v));

    return {
      labels,
      datasets: [
        {
          label: t("finance.balance"),
          data: running,
          borderColor: lineColor,
          backgroundColor: "rgba(75,192,192,0.15)",
          tension: 0.3,
          scales: {
            y: {
              grid: { color: gridColor },
            },
            x: {
              grid: { color: gridColor },
            },
          },
        },
      ],
    };
  }, [history, t]);

  // Create chart options with proper hook usage
  const chartOptions = useMemo(() => {
    // Create a formatting function that doesn't call the hook
    const formatTick = (value) => {
      if (value === undefined || value === null) return "";

      const formattedValue = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

      return `$${formattedValue}`;
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const idx = context.dataIndex;
              const entry = history?.[idx];
              const amt = Math.abs(Number(entry?.amount || 0));

              const balanceValue =
                context.parsed && typeof context.parsed.y === "number"
                  ? context.parsed.y
                  : undefined;

              const balanceLine = `${t("finance.balance", "Balance")}: ${
                balanceValue === undefined ? "" : formatCurrency(balanceValue)
              }`;

              if (!entry) return balanceLine;

              const isIncome = entry.type === "income";
              const deltaLabel = isIncome
                ? t("finance.income", "Income")
                : t("finance.spending", "Spending");
              const deltaSign = isIncome ? "+" : "-";
              const deltaLine = `${deltaLabel}: ${deltaSign}${formatCurrency(
                amt
              )}`;

              return [balanceLine, deltaLine];
            },
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: formatTick,
          },
          grid: { color: gridColor },
        },
        x: {
          grid: { color: gridColor },
        },
      },
    };
  }, [formatCurrency, gridColor, history, t]);

  // For the actual display, we'll update the ticks dynamically
  useEffect(() => {
    if (window.Chart && window.Chart.instances) {
      Object.values(window.Chart.instances).forEach((instance) => {
        if (instance.canvas.parentElement?.closest(".chart-container")) {
          instance.options.scales.y.ticks.callback = (value) =>
            formatCurrency(value);
          instance.update();
        }
      });
    }
  }, [formatCurrency]);

  return (
    <TrackerContainer>
      <Title>{t("finance.title")}</Title>

      {loading ? (
        <p>{t("finance.loading")}</p>
      ) : (
        <>
          <StatsContainer>
            <Stat type="income">
              {t("finance.totalIncome")}: {formatCurrency(totals.totalIncome)}
            </Stat>
            <Stat type="spending">
              {t("finance.totalSpending")}:{" "}
              {formatCurrency(totals.totalSpending)}
            </Stat>
            <Stat type="total">
              {t("finance.currentTotal")}: {formatCurrency(totals.currentTotal)}
            </Stat>
          </StatsContainer>

          <ButtonsRow>
            <PrimaryButton onClick={() => setShowIncomeModal(true)}>
              {t("finance.addIncome")}
            </PrimaryButton>
            <DangerButton onClick={() => setShowSpendingModal(true)}>
              {t("finance.addExpense")}
            </DangerButton>
          </ButtonsRow>

          <ChartArea className="chart-container">
            <Line data={chartData} options={chartOptions} />
          </ChartArea>
        </>
      )}

      {/* *** NEW: DETAILS LINK PLACED AT THE BOTTOM *** */}
      <DetailsLink
        href="/finances"
        onClick={() => console.log("Navigating to detailed finance history...")}
      >
        {t("finance.moreDetails")}
      </DetailsLink>

      {showIncomeModal && (
        <AddIncomeModal
          onClose={() => setShowIncomeModal(false)}
          onSave={async (value, note) => {
            await addIncome(value, note);
            setShowIncomeModal(false);
          }}
          formatCurrency={formatCurrency}
        />
      )}

      {showSpendingModal && (
        <AddSpendingModal
          userCategories={userCategories}
          onClose={() => setShowSpendingModal(false)}
          onSave={async (value, categoryName, note) => {
            await addSpending(value, categoryName, note);
            setShowSpendingModal(false);
          }}
          onAddCategory={addCategory}
          deleteCategory={deleteCategory}
          formatCurrency={formatCurrency}
        />
      )}
    </TrackerContainer>
  );
}

/* ---------------- Add Income Modal ---------------- */
function AddIncomeModal({ onClose, onSave, formatCurrency }) {
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const { t } = useTranslation();

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{t("finance.addIncome")}</ModalTitle>

        <Label>{t("finance.amount")}</Label>
        <NumberInputContainer>
          <CurrencySymbol>
            {formatCurrency(0).replace(/[\d.,]/g, "")}
          </CurrencySymbol>
          <NumberInput
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
          />
        </NumberInputContainer>

        <Label style={{ marginTop: 20 }}>{t("finance.note")}</Label>
        <NoteInput
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("finance.notePlaceholder")}
        />

        <ModalButtons>
          <ModalCancel onClick={onClose}>{t("finance.cancel")}</ModalCancel>
          <ModalSave
            onClick={() => onSave(Number(value), note)}
            disabled={!value}
          >
            {t("finance.save")}
          </ModalSave>
        </ModalButtons>
      </ModalCard>
    </ModalOverlay>
  );
}

/* ---------------- Add Spending Modal ---------------- */
function AddSpendingModal({
  onClose,
  onSave,
  onAddCategory,
  userCategories,
  deleteCategory,
  formatCurrency,
}) {
  const [value, setValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [note, setNote] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const { t } = useTranslation();

  const dropdownRef = useRef(null);

  // Default categories with icons
  const defaultCategories = [
    { id: "food", name: t("finance.categories.food"), icon: <FaUtensils /> },
    { id: "home", name: t("finance.categories.home"), icon: <FaHome /> },
    {
      id: "car",
      name: t("finance.categories.transportation"),
      icon: <FaCar />,
    },
    {
      id: "entertainment",
      name: t("finance.categories.entertainment"),
      icon: <IoGameController />,
    },
    {
      id: "education",
      name: t("finance.categories.education"),
      icon: <FaBook />,
    },
    {
      id: "Shopping",
      name: t("finance.categories.shopping"),
      icon: <FaGift />,
    },
    { id: "travel", name: t("finance.categories.travel"), icon: <FaPlane /> },
    {
      id: "health",
      name: t("finance.categories.health"),
      icon: <FaHeartbeat />,
    },
  ];

  // Merge Firestore categories with defaults (avoid duplicates)
  const firestoreCategories = (userCategories || []).map((cat) => ({
    id: cat.id,
    name: cat.name,
    icon: categoryIconMap[cat.name] ? (
      React.createElement(categoryIconMap[cat.name])
    ) : (
      <FaGem />
    ),
    isUserAdded: true,
  }));

  const categories = [
    ...defaultCategories.map((cat) => ({ ...cat, isUserAdded: false })),
    ...firestoreCategories.filter(
      (fc) => !defaultCategories.some((dc) => dc.name === fc.name)
    ),
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{t("finance.addExpense")}</ModalTitle>

        <Label>{t("finance.amount")}</Label>
        <NumberInputContainer>
          <CurrencySymbol>
            {formatCurrency(0).replace(/[\d.,]/g, "")}
          </CurrencySymbol>
          <NumberInput
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
          />
        </NumberInputContainer>

        <Label>{t("finance.category")}</Label>
        <div ref={dropdownRef} style={{ position: "relative" }}>
          <DropdownButton
            onClick={() => setDropdownOpen(!dropdownOpen)}
            type="button"
            aria-expanded={dropdownOpen}
          >
            {selectedCategory ? (
              <>
                {categories.find((c) => c.name === selectedCategory)?.icon}
                <span style={{ marginLeft: 8 }}>{selectedCategory}</span>
              </>
            ) : (
              t("finance.selectCategory")
            )}
          </DropdownButton>

          {dropdownOpen && (
            <DropdownList>
              <DropdownContent>
                {categories.map((c) => (
                  <DropdownItem
                    key={c.id}
                    onClick={() => {
                      setSelectedCategory(c.name);
                      setDropdownOpen(false);
                    }}
                  >
                    <CategoryContent>
                      {c.icon}
                      <span style={{ marginLeft: 12 }}>{c.name}</span>
                    </CategoryContent>

                    {c.isUserAdded && (
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCategory(c.id);
                        }}
                        aria-label={`Delete ${c.name} category`}
                      >
                        ×
                      </DeleteButton>
                    )}
                  </DropdownItem>
                ))}
              </DropdownContent>

              <CustomCategorySection>
                <Label style={{ margin: "0 0 8px 0", fontSize: "0.85rem" }}>
                  {t("finance.addNewCategory")}
                </Label>
                <InlineAddCategory>
                  <CustomCategoryInput
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder={t("finance.categoryPlaceholder")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <AddCategoryButton
                    disabled={!newCategory.trim()}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await onAddCategory(newCategory.trim());
                      setSelectedCategory(newCategory.trim());
                      setNewCategory("");
                      setDropdownOpen(false);
                    }}
                  >
                    {t("finance.add")}
                  </AddCategoryButton>
                </InlineAddCategory>
              </CustomCategorySection>
            </DropdownList>
          )}
        </div>

        <Label style={{ marginTop: 20 }}>{t("finance.note")}</Label>
        <NoteInput
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("finance.notePlaceholder")}
          rows="3"
        />

        <ModalButtons>
          <ModalCancel onClick={onClose}>{t("finance.cancel")}</ModalCancel>
          <ModalSave
            disabled={!value || !selectedCategory}
            onClick={() => onSave(Number(value), selectedCategory, note)}
          >
            {t("finance.save")}
          </ModalSave>
        </ModalButtons>
      </ModalCard>
    </ModalOverlay>
  );
}

/* ---------------- Styled Components ---------------- */
const TrackerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 0px 20px;
  background: var(--secondaryBackgroundColor);
  color: var(--fontPrimaryColor);
  height: 500px;
  border-radius: 20px;
  transition: 0.5s;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;

  .moreDetails {
    text-align: end;
    margin: 10px;
  }
`;

const Title = styled.h1`
  font-size: 18px;
  margin: 1em 0;
  font-weight: 400;
`;

const StatsContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 10px;
  text-align: left;
  font-size: 16px;
  width: 100%;
  @media (max-width: 500px) {
    font-size: 13px;
    flex-wrap: wrap;
  }
`;

const Stat = styled.p`
  margin: 0;
  padding: 5px 10px;
  color: var(--fontSecondaryColor);
  border-left: 4px solid
    ${(props) =>
      props.type === "income"
        ? "green"
        : props.type === "spending"
        ? "red"
        : "blue"};
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
`;

const PrimaryButton = styled.button`
  padding: 10px 16px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  min-height: 40px;
`;

const DangerButton = styled.button`
  padding: 10px 16px;
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  min-height: 40px;
`;

const ChartArea = styled.div`
  width: 100%;
  height: 240px;
  @media (max-width: 500px) {
    max-height: 200px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 9999;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: var(--popupColor);
  padding: 1.6rem;
  width: 500px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 550px) {
    width: 95%;
    max-width: 400px;
    padding: 1.2rem;
    margin: 0 auto;
  }

  @media (max-width: 380px) {
    width: 100%;
    border-radius: 16px;
    max-height: 95vh;
  }

  select {
    padding: 15px 20px;
    border-radius: 5px;
    border: none;
    font-family: "Montserrat", sans-serif;
    font-size: 0.9rem;
    background: var(--thirdBackgroundColor);
    outline: none;
    color: var(--fontPrimaryColor);
    cursor: pointer;
    option {
      padding: 15px 20px;
      height: 100px;
    }
  }
`;

const ModalTitle = styled.h3`
  margin: 1em 0 2em 0;
  text-align: center;
  font-weight: 400;
`;

const Label = styled.p`
  opacity: 0.7;
  font-size: 0.9rem;
  border: none;
`;

const NumberInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 20px;
  color: var(--fontSecondaryColor);
  font-size: 0.9rem;
  z-index: 1;
`;

const NumberInput = styled.input`
  width: 100%;
  padding: 15px 20px 15px 40px;
  border-radius: 5px;
  border: none;
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  background: var(--thirdBackgroundColor);
  outline: none;
  color: var(--fontPrimaryColor);
  cursor: pointer;
  box-shadow: 0 0 10px 1px var(--popupShadow);
  min-height: 48px;

  &::placeholder {
    color: #cecece;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 15px 20px;
  border-radius: 5px;
  border: none;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: var(--thirdBackgroundColor);
  outline: none;
  color: var(--fontPrimaryColor);
  cursor: pointer;
  box-shadow: 0 0 10px 1px var(--popupShadow);
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  text-align: left;
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--thirdBackgroundColor);
  border-radius: 8px;
  border: 1px solid #545f7c;
  max-height: min(80vh, 500px);
  overflow: hidden;
  z-index: 1000;
  margin-top: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  @media (max-height: 600px) {
    max-height: 60vh;
  }

  @media (max-width: 480px) {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 16px 16px 0 0;
    max-height: 70vh;
    margin-top: 0;
    animation: slideUp 0.3s ease;
  }
`;

const DropdownContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: #48506a transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #48506a;
    border-radius: 2px;
  }
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: var(--fontPrimaryColor);
  justify-content: space-between;
  min-height: 48px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  /* Active state for touch devices */
  &:active {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const CategoryContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const CustomCategorySection = styled.div`
  border-top: 1px solid #48506a;
  padding: 16px;
  background-color: rgba(40, 48, 70, 0.5);
  position: sticky;
  bottom: 0;
`;

const InlineAddCategory = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const CustomCategoryInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #545f7c;
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  font-size: 0.9rem;
  min-height: 44px;

  &::placeholder {
    color: #a0a0a0;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const AddCategoryButton = styled.button`
  padding: 12px 24px;
  background: #38bdf8;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  min-height: 44px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  font-size: 1.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background-color: rgba(239, 68, 68, 0.1);
  }
`;

const NoteInput = styled.textarea`
  padding: 15px 20px;
  border-radius: 5px;
  border: none;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: var(--thirdBackgroundColor);
  outline: none;
  color: var(--fontPrimaryColor);
  box-shadow: 0 0 10px 1px var(--popupShadow);
  cursor: pointer;
  resize: vertical;
  min-height: 80px;

  &::placeholder {
    color: #c7c6c6;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 1em;
`;

const ModalCancel = styled.button`
  padding: 12px 20px;
  background: transparent;
  border: 1px solid #ff00002b;
  background-color: #ff00002b;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
  min-width: 80px;
`;

const ModalSave = styled.button`
  padding: 12px 20px;
  background: #191e2b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  min-width: 80px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Add global styles for animation
const GlobalStyles = () => (
  <style>{`
    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
    
    /* Improve touch targets on mobile */
    @media (max-width: 480px) {
      button, input, textarea, select {
        font-size: 16px !important; /* Prevents zoom on iOS */
      }
    }
  `}</style>
);

// Don't forget to render GlobalStyles in your app
// You can add this to your main App component
